import React, { useState, useEffect } from 'react';
import { ShieldCheck, ChevronDown, ChevronUp } from 'lucide-react';
import PageLayout from '../../components/PageLayout';
import { useAuth } from '../../context/AuthContext';
import DataGrid from '../../components/DataGrid';
import { auditLogs as api, type AuditLogEntry } from '../../services/api';

const inp = {
  padding: '9px 12px', background: 'var(--nx-surface-3)',
  border: '1px solid var(--nx-border)', borderRadius: 8, fontSize: 13,
  color: 'var(--nx-text-primary)', outline: 'none', boxSizing: 'border-box' as const,
};

const AuditLogsPage: React.FC = () => {
  const { user } = useAuth();
  const societyId = user?.societyId ?? null;
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [action, setAction] = useState('');
  const [targetType, setTargetType] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const refresh = () => {
    if (!societyId) { setLoading(false); return; }
    setLoading(true);
    api.list(societyId, { action: action || undefined, targetType: targetType || undefined, page })
      .then(res => { setEntries(res.content); setTotalPages(res.totalPages); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(refresh, [societyId, page, action, targetType]);

  return (
    <PageLayout>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: 22, fontWeight: 700, color: 'var(--nx-text-primary)', letterSpacing: '-0.03em', marginBottom: 4 }}>
          Audit Logs
        </h1>
        <p style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>Immutable, insert-only record of high-value actions across finance, staff, procurement and helpdesk.</p>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input placeholder="Filter by action (e.g. INVOICE_PAID)" value={action} onChange={e => { setPage(0); setAction(e.target.value); }} style={{ ...inp, minWidth: 240 }} />
        <input placeholder="Filter by target type (e.g. Invoice)" value={targetType} onChange={e => { setPage(0); setTargetType(e.target.value); }} style={{ ...inp, minWidth: 220 }} />
      </div>

      <div className="nx-card" style={{ padding: 0, overflow: 'hidden' }}>
        <DataGrid
          columns={[
            { key: 'time', header: 'Time', cellStyle: { color: 'var(--nx-text-muted)', whiteSpace: 'nowrap' }, render: (e: AuditLogEntry) => new Date(e.createdAt).toLocaleString() },
            { key: 'action', header: 'Action', cellStyle: { fontWeight: 500 }, render: (e: AuditLogEntry) => e.action },
            { key: 'target', header: 'Target', render: (e: AuditLogEntry) => e.targetType ? `${e.targetType}${e.targetId ? ` (${e.targetId.slice(0, 8)})` : ''}` : '—' },
            { key: 'user', header: 'Actor', cellStyle: { color: 'var(--nx-text-muted)' }, render: (e: AuditLogEntry) => e.userId ? e.userId.slice(0, 8) : 'system' },
            {
              key: 'payload', header: '',
              render: (e: AuditLogEntry) => e.payload ? (
                <button
                  className="nx-btn nx-btn-outline nx-btn-sm"
                  onClick={() => setExpandedId(p => p === e.id ? null : e.id)}
                  style={{ gap: 4 }}
                >
                  {expandedId === e.id ? <ChevronUp size={12} /> : <ChevronDown size={12} />} Details
                </button>
              ) : null,
            },
          ]}
          rows={entries}
          rowKey={e => e.id}
          loading={loading}
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          emptyIcon={<ShieldCheck size={32} style={{ color: 'var(--nx-text-muted)' }} />}
          emptyText="No audit entries yet."
        />
      </div>

      {expandedId && (
        <div className="nx-card" style={{ padding: 16, marginTop: 12 }}>
          <pre style={{ fontSize: 12, color: 'var(--nx-text-secondary)', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
            {entries.find(e => e.id === expandedId)?.payload}
          </pre>
        </div>
      )}
    </PageLayout>
  );
};

export default AuditLogsPage;
